// Types for Yandex Metrica
declare global {
  interface Window {
    ym?: (
      counterId: number,
      method: string,
      ...params: any[]
    ) => void;
    // Ecommerce data container. Yandex Metrica is initialized with
    // ecommerce:"dataLayer" (see public/index.html), so it consumes the same
    // container that GA4 uses.
    dataLayer?: Record<string, any>[];
  }
}

const YANDEX_METRIKA_ID = 104392472;

/**
 * Track user login
 * @param method - Login method (email, google, telegram)
 */
export const trackUserLogin = (method: 'email' | 'google' | 'telegram') => {
  if (typeof window !== 'undefined' && window.ym) {
    window.ym(YANDEX_METRIKA_ID, 'reachGoal', 'user_login', {
      method: method
    });
    // console.log(`📊 [Yandex Metrica] Event sent: user_login (${method})`);
  } else {
    // console.warn('⚠️ [Yandex Metrica] Failed to send user_login event - counter not loaded');
  }
};

/**
 * Track user signup
 * @param method - Signup method (email, google, telegram)
 */
export const trackUserSignup = (method: 'email' | 'google' | 'telegram') => {
  if (typeof window !== 'undefined' && window.ym) {
    window.ym(YANDEX_METRIKA_ID, 'reachGoal', 'user_signup', {
      method: method
    });
    // console.log(`📊 [Yandex Metrica] Event sent: user_signup (${method})`);
  } else {
    // console.warn('⚠️ [Yandex Metrica] Failed to send user_signup event - counter not loaded');
  }
};

/**
 * Track IP check
 * @param type - Check type (my_ip, custom_ip)
 */
export const trackIPCheck = (type: 'my_ip' | 'custom_ip') => {
  if (typeof window !== 'undefined' && window.ym) {
    window.ym(YANDEX_METRIKA_ID, 'reachGoal', 'ip_check', {
      type: type
    });
    // console.log(`📊 [Yandex Metrica] Event sent: ip_check (${type})`);
  }
};

/**
 * Track subscription purchase
 * @param plan - Plan type (basic, premium, etc.)
 */
export const trackSubscriptionPurchase = (plan: string) => {
  if (typeof window !== 'undefined' && window.ym) {
    window.ym(YANDEX_METRIKA_ID, 'reachGoal', 'subscription_purchase', {
      plan: plan
    });
    // console.log(`📊 [Yandex Metrica] Event sent: subscription_purchase (${plan})`);
  }
};

type EcommerceProduct = {
  id: string;
  name: string;
  price?: number;
  quantity?: number;
  category?: string;
  brand?: string;
};

/**
 * Push an ecommerce "purchase" event into window.dataLayer.
 *
 * The container is read by Yandex Metrica (counter 104392472, initialized with
 * ecommerce:"dataLayer") and by GA4. Currency must be an ISO 4217 3-letter code,
 * otherwise Metrica records zeros for amount/revenue.
 *
 * @param transactionId - Unique purchase/order id (used by Metrica as the goal id)
 * @param revenue - Total revenue for the purchase
 * @param currency - ISO 4217 currency code (e.g. "USD")
 * @param products - Line items associated with the purchase
 */
export const trackEcommercePurchase = (params: {
  transactionId: string;
  revenue: number;
  currency: string;
  products: EcommerceProduct[];
}) => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ecommerce: {
      currencyCode: params.currency,
      purchase: {
        actionField: {
          id: params.transactionId,
          revenue: params.revenue,
        },
        products: params.products,
      },
    },
  });
  // console.log(`📊 [Ecommerce] purchase pushed: ${params.transactionId} (${params.revenue} ${params.currency})`);
};

/**
 * Track page view
 * @param url - Page URL
 * @param title - Page title
 */
export const trackPageView = (url: string, title?: string) => {
  if (typeof window !== 'undefined' && window.ym) {
    window.ym(YANDEX_METRIKA_ID, 'hit', url, {
      title: title
    });
    // console.log(`📊 [Yandex Metrica] Page view sent: ${url}`);
  }
};

